// Dev-only stored-data shape guard.
//
// Helm persists a bunch of state across page loads — operator
// identity (`helm:*`), the offline-authored studies cache
// (`usability-suite-v2:studies`), the metadata sidecar
// (`usability-suite-v2:metadata`), participant-bindings, plus the
// SharedWorker broker's IndexedDB snapshot in IPC mode. When a
// developer bumps a stored shape incompatibly mid-prototyping (a
// new required field on `MetadataEntry`, a renamed key in a study
// payload), every existing branch carrying old data on disk crashes
// the new code with a parse error or a TypeScript-shaped object
// that's missing a field.
//
// Until we ship real migrations, this module is the stop-gap:
//
//   - Bump `STORAGE_SCHEMA_VERSION` whenever a stored shape changes
//     incompatibly. Production builds DO NOT auto-wipe (we'd never
//     ship that to a real lab); production just records the version.
//   - In dev builds, mismatch → wipe everything and reload. The
//     <ClearLocalStorageButton> calls the same `clearAllStorage()`
//     helper so the manual + automatic paths stay in sync.
//   - When real migration support arrives, this guard goes away —
//     the version number becomes the input to a per-version
//     migration table.

const SCHEMA_VERSION_KEY = 'helm:schema-version';

/**
 * Bump this whenever a stored shape changes incompatibly. Dev
 * builds clear every local store on mismatch; production builds
 * just update the recorded version (a real lab's data is not
 * something we should silently delete).
 *
 * Changelog:
 *   1 — initial version
 */
export const STORAGE_SCHEMA_VERSION = 1;

/** IndexedDB databases Helm creates. Wiping these covers IPC mode
 *  (the SharedWorker broker persists its snapshot here) — the
 *  list is short on purpose; add new entries as new persistent
 *  stores land. */
const HELM_IDB_DATABASES = ['helm-broker'];

/**
 * Drop everything Helm has on disk for this origin — localStorage,
 * sessionStorage, and the broker's IndexedDB snapshot. Best-effort;
 * private-mode browsers can throw on `clear()`, and IDB deletes
 * resolve asynchronously, so callers that need a clean boot should
 * `await` this and then reload.
 *
 * Used by:
 *   - <ClearLocalStorageButton> (manual dev affordance).
 *   - `checkAndClearOnVersionMismatch()` at boot when the recorded
 *     schema version doesn't match this build's.
 */
export async function clearAllStorage(): Promise<void> {
  try {
    window.localStorage.clear();
  } catch {
    // ignore — private mode / quota-exceeded
  }
  try {
    window.sessionStorage.clear();
  } catch {
    // ignore
  }
  await clearIndexedDB();
}

async function clearIndexedDB(): Promise<void> {
  // IDB lives behind `globalThis.indexedDB` in the window context.
  // Guard so a server-side render or older browser doesn't trip.
  if (typeof indexedDB === 'undefined') return;
  await Promise.all(
    HELM_IDB_DATABASES.map(
      (name) =>
        new Promise<void>((resolve) => {
          // `deleteDatabase` blocks on connections held by other
          // tabs. We don't wait for `onblocked` — the user clicking
          // "clear" implies they accept the risk of an open second
          // tab keeping a copy. `onsuccess` and `onerror` both
          // resolve so the boot path doesn't hang on a stuck delete.
          let settled = false;
          const settle = () => {
            if (settled) return;
            settled = true;
            resolve();
          };
          try {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = settle;
            req.onerror = settle;
            req.onblocked = settle;
          } catch {
            settle();
          }
        }),
    ),
  );
}

/**
 * Boot-time schema-version guard. Call once before React renders.
 *
 * Behaviour matrix:
 *
 *   Mode      Stored version  Action
 *   ────────  ─────────────  ─────────────────────────────────────
 *   prod      any             Write current version; do not wipe
 *   prod      matches         No-op
 *   dev       absent          Write current version (first run)
 *   dev       matches         No-op
 *   dev       mismatched      Wipe everything + write current +
 *                             reload (`returnHash` lands the page
 *                             at "#/" so the user doesn't reload
 *                             back into a now-empty study editor)
 *
 * Returns a Promise so callers can `await` it; the resolve value
 * is `true` if a wipe + reload was triggered (which means the
 * caller should NOT proceed with rendering — the reload's about
 * to interrupt anything that comes next), `false` otherwise.
 */
export async function checkAndClearOnVersionMismatch(opts: {
  isDev: boolean;
  returnHash?: string;
}): Promise<boolean> {
  const current = String(STORAGE_SCHEMA_VERSION);
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(SCHEMA_VERSION_KEY);
  } catch {
    // ignore — if reads fail, writes will too; nothing to do
    return false;
  }

  // Production never wipes. Just record the version on first run
  // so a future mismatch can be detected if we ever expose this
  // capability outside dev.
  if (!opts.isDev) {
    if (stored !== current) {
      try {
        window.localStorage.setItem(SCHEMA_VERSION_KEY, current);
      } catch {
        // ignore
      }
    }
    return false;
  }

  // Dev path.
  if (stored === current) return false;
  // First-run sentinel: no version recorded yet AND no other Helm
  // keys present. Just record the version without wiping — a fresh
  // browser doesn't need a reload-loop on its first visit.
  if (stored === null && !hasAnyHelmKey()) {
    try {
      window.localStorage.setItem(SCHEMA_VERSION_KEY, current);
    } catch {
      // ignore
    }
    return false;
  }
  // Real mismatch (or downgrade across an upgrade): wipe + reload.
  await clearAllStorage();
  try {
    window.localStorage.setItem(SCHEMA_VERSION_KEY, current);
  } catch {
    // ignore
  }
  if (opts.returnHash !== undefined) {
    window.location.hash = opts.returnHash;
  }
  window.location.reload();
  return true;
}

/** Cheap heuristic for "is this a fresh browser?" — looks for any
 *  `helm:*` or `usability-suite-v2:*` key. Used to suppress the
 *  spurious wipe-and-reload on a brand-new visit where the version
 *  is absent because nothing's been stored yet. */
function hasAnyHelmKey(): boolean {
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('helm:') || k.startsWith('usability-suite-v2:')) {
        return true;
      }
    }
  } catch {
    // ignore
  }
  return false;
}
